import React, { useEffect, useMemo, useRef, useState } from "react";
import { withDocBridge } from "~/design-system/components/DeckGen/birtvy/docBridge";
import { onBrandFacesChange } from "~/design-system/components/DeckGen/utils/brandFonts";
import { NO_RECORDING } from "~/lib/noRecording";
import { useInlinedDeckImages } from "./inlineDeckImages";
import {
  clearArtifactSelfWrite,
  isArtifactSelfWrite,
  registerArtifactFrame,
} from "./artifactFrameBus";

/**
 * The artifact's own coordinate space. Identical to `artifactStage`'s `ARTIFACT_W/H` and read from
 * Birtvy directly rather than from that file, because that file imports this one.
 */
const SURFACE: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  border: 0,
  margin: 0,
  padding: 0,
  display: "block",
  boxSizing: "border-box",
  overflow: "hidden",
  // The workspace preview is the artifact itself, so its authored CSS animations, timers and event
  // handlers must receive a live browsing context and pointer input.
  pointerEvents: "auto",
};

/**
 * The same surface, before the document has painted.
 *
 * `background: transparent` is belt and braces, not the fix. MEASURED 2026-09-10, on a sandboxed
 * opaque-origin frame held mid-parse by a blocking script over a coloured parent: the frame shows the
 * PARENT through it for the whole of that window, with and without a background of its own. So an
 * unpainted iframe was never the white — the frame's own paper was, and that paper is gone
 * (`ArtifactFrame.module.css`, removed 2026-09-10). This stays because a frame that will paint no
 * ground of its own must not acquire one here either.
 */
const SURFACE_PENDING: React.CSSProperties = {
  ...SURFACE,
  background: "transparent",
};

/**
 * The OUTGOING document, still on screen over the incoming one.
 *
 * `zIndex` rather than DOM order, and that is not a style preference: moving an iframe in the DOM
 * reloads it, so the held frame has to keep the exact slot it was mounted in and be lifted over the
 * new one by paint order instead. `pointerEvents: none` because it is a picture of the artifact you
 * were looking at a moment ago — clicking it would drive a document that is on its way out.
 */
const SURFACE_HELD: React.CSSProperties = {
  ...SURFACE,
  zIndex: 1,
  pointerEvents: "none",
};

/**
 * How long the outgoing document may stay up.
 *
 * `srcDoc` always fires `load`, so in practice the hold ends in about a tenth of a second. This is for
 * the case that cannot happen: a frame that never reports, which would otherwise leave the PREVIOUS
 * artifact on a pane whose title, size and toolbar all name the new one. A stale picture is worse than
 * a bare pane, so after this the pane tells the truth.
 */
const HOLD_MAX_MS = 2000;

export interface ArtifactDocumentProps {
  /** `BirtvySlide.docHtml` — a COMPLETE self-contained HTML document, authored at 1920x1080. */
  html: string;
  /** `BirtvySlide.docBaked` — retained for the static canvas/thumbnails, but intentionally not used by this live preview. */
  baked?: string;
  /** For the iframe's accessible name. */
  title: string;
}

/**
 * An artifact on the stage: always the live document in a sandboxed iframe.
 *
 * ## Why both, and why this is not `DocSlideFrame`
 *
 * The canvas's `DocSlideFrame` (`birtvy/BirtvySlideCard.tsx`) is the reference for both mechanisms and
 * this is a deliberately smaller equivalent, written here rather than lifted, because that component is
 * wired into canvas state that has no counterpart in this route: the global bake queue and its viewport
 * priority, the `{__bfz}` freeze bridge, `docStream`'s streaming shell, the logo-fill post-pass, the
 * 250ms hover-intent gate, the warm-hold tier, and an `onBaked` callback that patches the blob. Lifting
 * it would have meant either editing that file or dragging all of it along. What is kept is the part
 * that decides what the user sees:
 *
 * - **Always live → a sandboxed iframe, `sandbox="allow-scripts"` and NO `allow-same-origin`.** The
 *   workspace is where an artifact is viewed as an artifact, not as a thumbnail: authored CSS
 *   animations, requestAnimationFrame/setInterval loops and click interactions must remain alive.
 *   The opaque origin is still intentional, so model-authored content cannot reach this origin's
 *   cookies, storage or DOM. This is the same sandbox contract as `artifactAgent/ArtifactAgentPane`.
 * - **The baked string remains a canvas concern.** `docBridge` deliberately finishes animations,
 *   freezes infinite motion and strips scripts when it creates `docBaked`; that is correct for the
 *   canvas resting tier and thumbnails, but wrong for this preview surface.
 *
 */
export function ArtifactDocument({ html, title }: ArtifactDocumentProps) {
  /**
   * The source the MOUNTED document was built from — which is not always the latest source.
   *
   * THIS IS THE WHOLE FIX FOR "the artifact reloads on every edit", and the reason the generation
   * counter alone was not enough. Keying the iframe by generation stops React REPLACING the element,
   * but React still updates the attributes of the element it kept — and `srcDoc` is not an ordinary
   * attribute: assigning it navigates the frame. So an adopted-in-place edit skipped the remount and
   * then reloaded the document anyway, one attribute later, taking the caret, the selection and the
   * artifact's entrance animations with it on every landed keystroke.
   *
   * So the frame is fed the generation's OWN source and nothing else. When this app writes an edit,
   * the document already shows it — the bridge applied it live before the commit went anywhere — so
   * there is nothing to re-render and the string is left exactly as the frame received it. A genuinely
   * new document bumps the generation, and only then does this move.
   */
  const genHtmlRef = useRef(html);

  // One generation per source string, so a rewrite mounts a FRESH document and its entrance animations
  // replay instead of a new `srcDoc` being written into the mounted one. Render-phase ref bump, which
  // is idempotent under StrictMode's double render — the canvas does the same, for the same reason,
  // and keys on the counter rather than on the source because the source is the size of a small file.
  const genRef = useRef(0);
  const prevHtmlRef = useRef(html);
  /**
   * The last document that ACTUALLY PAINTED, and the one that is being held over the new one.
   *
   * Two refs and not one, because rapid switching must not hold a blank: if you move off an artifact
   * before its document painted, the thing worth keeping on screen is still the last one that did, not
   * the one that was mid-parse. `lastPaintedRef` only ever moves on a ready frame (see the assignment
   * just above the return), so `heldRef` picking it up at a bump gets a document that is known to have
   * pixels — and its generation, so the frame keeps the key it was mounted with and is not reloaded.
   */
  const lastPaintedRef = useRef<{ gen: number; html: string } | null>(null);
  const heldRef = useRef<{ gen: number; html: string } | null>(null);

  if (prevHtmlRef.current !== html) {
    prevHtmlRef.current = html;
    // ...unless this app wrote it on the document's behalf. An inline text edit commits a new source
    // for pixels the user is already looking at, and remounting there would take the caret out of the
    // sentence being typed. See `expectArtifactSelfWrite`. Everything else — a regeneration, a
    // re-theme, an undo — is a genuinely different document and still gets a fresh frame.
    if (isArtifactSelfWrite(html)) {
      // Adopted in place — the pixels already show it, because this app is what changed them. The
      // frame keeps the source it was mounted with; see `genHtmlRef`.
    } else {
      // What is on screen now stays on screen until the new document has painted. This is the whole of
      // the no-flash contract: the pane's own ground is never uncovered, so there is nothing to flash.
      heldRef.current = lastPaintedRef.current;
      genRef.current += 1;
      genHtmlRef.current = html;
      // A different document is on screen now, so the standing expectation is about a source that is
      // no longer here. Clearing it is what stops it matching again if the user comes back — see
      // `clearArtifactSelfWrite`.
      clearArtifactSelfWrite();
    }
  }

  /*
   * THE BRAND'S REAL TYPEFACE arrives late. `withDocBridge` injects the active kit's `@font-face` rules
   * into the document, but the kit comes over the network (`primeBrandFonts`, fired by the first bridge
   * build that finds no faces), so a deck opened cold builds its first document with no faces and then
   * never rebuilds it: the memo below keys on the generation, and nothing moved it. MEASURED on staging,
   * 2026-09-11: the host document held every anthropicSans face and the frame's source carried none, so
   * every heading drew in the system fallback while the toolbar named the right family. The Birtvy
   * editor already handles this by subscribing and rebuilding once (`BirtvyArtifactEditor`); this is
   * the same move. A faces change is treated as a new generation and goes through the held-frame path,
   * so the reader sees the old document until the re-fonted one has painted, never a blank.
   * `setActiveBrandFaces` notifies only on a real change, so this fires once per kit, not per render.
   */
  const [facesTick, setFacesTick] = useState(0);
  useEffect(() => onBrandFacesChange(() => setFacesTick((t) => t + 1)), []);
  const prevFacesTickRef = useRef(facesTick);
  if (prevFacesTickRef.current !== facesTick) {
    prevFacesTickRef.current = facesTick;
    heldRef.current = lastPaintedRef.current;
    genRef.current += 1;
    // The re-fonted frame mounts the source AS IT STANDS. `genHtmlRef` still holds the one this
    // generation was mounted with, and every self-write since (typed words, a hand edit, a picture the
    // user added) lives only in the blob and the live frame, so remounting that would drop them all.
    genHtmlRef.current = prevHtmlRef.current;
  }

  // Memoised on the GENERATION, not on `html`: two different sources can be on screen at once in the
  // sense that matters here — the one the frame is showing and the one the blob now holds — and this
  // is the former.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const liveHtml = useMemo(
    () => withDocBridge(genHtmlRef.current),
    [genRef.current]
  );
  // A placed image's stored URL cannot be fetched from inside the sandbox; the page fetches it and the
  // frame gets the bytes. Same string back when the document holds no placed image.
  const shownHtml = useInlinedDeckImages(liveHtml);

  /*
   * WHICH GENERATION HAS PAINTED — and why readiness is DERIVED from it rather than kept as a flag.
   *
   * An iframe is mounted the instant `docHtml` exists, but `srcDoc` still has to parse and paint, and
   * whatever is behind it is drawn from the first frame. That gap used to be a white slide in the
   * reader's face ahead of a dark artifact (Beka, 2026-09-04). It is now what `held` covers — and what
   * `held` is keyed off is this.
   *
   * This was a `useState(false)` reset from a render-phase marker: a generation bump moved the marker
   * and queued `setReady(false)` in the same render that produced the new key. That reset has to LAND
   * in the commit that mounts the new iframe — and in the workspace it did not. Measured on the real
   * app (Beka, 2026-09-10) by sampling the frame and the frame's then-white paper every animation
   * frame across a switch: the iframe element WAS replaced, and every new element was already `READY`
   * at its first sample, with the paper never leaving `rgb(255, 255, 255)`. The same probe against
   * `SwitchingArtifacts` in Storybook showed the window correctly — 154ms of `pending` over
   * `rgba(0, 0, 0, 0)` — which is what made it a reset that was missed rather than a gate that was
   * wrong. A flash is frame-quantised exactly as the probe is, so a flash nobody sampled as pending
   * was never pending.
   *
   * A DERIVED value cannot be missed. `loadedGen` names the generation whose document has actually
   * painted and nothing else, so the moment `genRef` moves this reads false again — with nothing to
   * schedule, nothing to coalesce, and no commit to land in. A fresh mount starts at -1, which is not
   * any generation, so a remount needs no separate handling.
   *
   * The counter is also the only shape that can say "a DIFFERENT document finished loading". This
   * component is reused across artifact switches (`StageBoundary` resets error state, it does not
   * remount its children), and a boolean going true a second time is a no-op — the registration effect
   * below would never re-run and the frame would stay registered to the PREVIOUS artifact's window,
   * which is why text editing worked on the first artifact opened and on none after it. Generations
   * only increase, so a load always moves this, and it carries the answer to a second question for
   * free: whether the window that just loaded is still the one on screen, or whether another switch
   * has already overtaken it.
   */
  const [loadedGen, setLoadedGen] = useState(-1);
  const ready = loadedGen === genRef.current;
  /** The pending paint gate — both handles, so a replaced iframe cannot reveal anything after it is
   *  gone and an unmount leaves nothing running. See the `onLoad` note. */
  const paintRef = useRef<{ raf: number; timer: number }>({ raf: 0, timer: 0 });
  useEffect(
    () => () => {
      cancelAnimationFrame(paintRef.current.raf);
      window.clearTimeout(paintRef.current.timer);
    },
    []
  );
  const frameRef = useRef<HTMLIFrameElement | null>(null);

  /**
   * THE HOLD.
   *
   * Gating the frame's white paper off `data-ready` fixed the white card and left a grey one — the
   * pane's own ground, uncovered for as long as the incoming document takes to parse (Beka,
   * 2026-09-10: "now it's grey"). Suppressing a surface was always the wrong shape of answer: for the
   * eye there is no good colour for "the artifact you asked for is not here yet", and every one of
   * them reads as a flash. The paper itself has since gone (`ArtifactFrame.module.css`); this is what
   * covers the gap it used to cause and the pane would otherwise show.
   *
   * So nothing is suppressed. The document you were looking at stays up, at full opacity, over the new
   * frame, until the new frame reports that it has painted — the same thing a browser does when you
   * follow a link, and the reason that does not flash either. The incoming frame is mounted UNDER it
   * and fully visible rather than hidden, so it is genuinely rasterised while it is covered; there is
   * no reveal to get wrong.
   *
   * `holdEndedGen` is the escape hatch, not the mechanism — see `HOLD_MAX_MS`.
   */
  const [holdEndedGen, setHoldEndedGen] = useState(-1);
  const held =
    ready || holdEndedGen === genRef.current ? null : heldRef.current;
  useEffect(() => {
    if (ready) {
      // Nothing is being covered any more, and a held document is a whole HTML file to keep alive.
      heldRef.current = null;
      return;
    }
    const timer = window.setTimeout(
      () => setHoldEndedGen(genRef.current),
      HOLD_MAX_MS
    );
    return () => window.clearTimeout(timer);
  }, [ready, loadedGen]);

  /**
   * Publish the live frame for the surfaces that have to TALK to this document — annotate mode, which
   * asks what is under a point, and inline text editing, which drives the bridge. They cannot reach it
   * any other way: `stageFor` is a pure function and chooses this component from inside a switch, so
   * there is no ref path from them to here. See `artifactFrameBus`.
   *
   * Gated on `ready` because before the document's own load there is no bridge listening inside it, so
   * a message posted then goes nowhere and a hit-test would time out rather than answer. `ready` also
   * resets on every generation, which is exactly when a reader's element paths stop being valid — the
   * generation rides along so it can tell that it has to measure again.
   */
  useEffect(() => {
    if (loadedGen !== genRef.current) return;
    const win = frameRef.current?.contentWindow;
    if (!win) return;
    return registerArtifactFrame(win, genRef.current);
  }, [loadedGen]);

  /**
   * Remember what is on screen, but ONLY once it has painted — this is what `heldRef` picks up at the
   * next generation, and holding a document that never painted would hold a blank.
   */
  if (ready && shownHtml !== null)
    lastPaintedRef.current = { gen: genRef.current, html: shownHtml };

  /**
   * The outgoing document, rendered FIRST in both branches below.
   *
   * Its slot is the point. React reconciles these children by key, and a keyed child that keeps its
   * relative position is never re-inserted — which matters more here than anywhere else in the app,
   * because re-inserting an iframe RELOADS it, and a held frame that reloads is a blank frame, which is
   * the flash again with extra steps. Held first, incoming appended after it, in both returns.
   */
  const heldFrame = held ? (
    <iframe
      key={`artifact${held.gen}`}
      srcDoc={held.html}
      sandbox="allow-scripts"
      className={NO_RECORDING}
      title={title}
      style={SURFACE_HELD}
      aria-hidden
      tabIndex={-1}
      data-artifact-held
    />
  ) : null;

  // Bytes still on their way for a placed image: no frame yet. With a document held over it the reader
  // sees the artifact they were on rather than the pending surface, for a fetch's worth longer.
  if (shownHtml === null) {
    return (
      <>
        {heldFrame}
        <div
          key="pending"
          style={SURFACE_PENDING}
          aria-busy
          data-artifact-pending
        />
      </>
    );
  }

  return (
    <>
      {heldFrame}
      <iframe
        key={`artifact${genRef.current}`}
        ref={frameRef}
        srcDoc={shownHtml}
        // No `allow-same-origin`. See the component doc: the opaque origin is the whole point.
        sandbox="allow-scripts"
        // Kept out of session recording: it would copy and gzip the whole document on every switch.
        className={NO_RECORDING}
        title={title}
        // Transparent until the document paints. Once `srcDoc` has painted, the artifact's own
        // background is what covers this; for one that paints none, what is behind it is the pane.
        style={ready ? SURFACE : SURFACE_PENDING}
        data-ready={ready ? "" : undefined}
        /**
         * `load` IS NOT `PAINTED`, and the difference is the whole of the flash.
         *
         * `ready` used to be set here, synchronously. But `load` fires when the document and its
         * subresources have been FETCHED — it says nothing about whether a single pixel of the artifact
         * has been drawn. The whole hold hangs off this flag — the outgoing document is dropped the
         * moment it goes true — so opening it at load swaps to a frame that has fetched but not drawn,
         * which is exactly the gap the hold exists to close. Switching between artifacts is where it
         * shows, because there is no loader covering it.
         *
         * MEASURED, 2026-09-10: on a switch the new iframe mounts at +12ms and `load` fires at +99ms.
         * The frame's paper — white, back when the frame painted one — turned on that second event
         * with the document still blank.
         *
         * Two animation frames is the same gate the bridge uses for the same question — `whenRendered`
         * in `docBridge`, which waits exactly this long before reading a document it needs to be sure
         * has rendered. One frame schedules the paint; the second lands after it.
         *
         * The generation is captured at LOAD, before the wait, so a switch arriving during those two
         * frames cannot make a stale window look current — and the effect is cancelled on unmount so a
         * frame that is replaced mid-wait never reveals anything.
         */
        onLoad={() => {
          const loadedAt = genRef.current;
          const reveal = () => {
            cancelAnimationFrame(paintRef.current.raf);
            window.clearTimeout(paintRef.current.timer);
            // The frame this belongs to may have been replaced while we waited.
            if (genRef.current !== loadedAt) return;
            setLoadedGen(loadedAt);
          };
          /**
           * TWO ANIMATION FRAMES, OR 120ms — whichever comes first, and the second half is not padding.
           *
           * `requestAnimationFrame` does not run in a hidden or fully occluded document, so a gate built
           * on it alone can wait forever: the outgoing document would be held over the new one
           * indefinitely, so the pane would go on showing the artifact you just left. Measured while
           * writing this: in a backgrounded pane the rAF pair had not fired after 1500ms.
           *
           * So rAF is the fast path and the timer is the floor. Whichever wins cancels the other.
           */
          paintRef.current.raf = requestAnimationFrame(() => {
            paintRef.current.raf = requestAnimationFrame(reveal);
          });
          paintRef.current.timer = window.setTimeout(reveal, 120);
        }}
      />
    </>
  );
}
